import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useSubscriptions from "@/hooks/useSubscriptions";
import MessageScrubbingPanel from "./MessageScrubbing";
import AgentIdlePanel from "./AgentIdle";
import CustomStatusPanel from "./CustomStatuses";
import SecurityPanel from "./Security";
import IpBlockingPanel from "./IpBlocking";
import AgentIpAllowlistPanel from "./AgentIpAllowlist";
import DataRetentionPanel from "./DataRetention";
import ContactPanelSettingsPanel from "./ContactPanel";
import ReportSettingsPanel from "./ReportSettings";
// Canonical activity log lives in /admin/activity-log (richer schema: userEmail,
// category pills, workflow drill-down timeline). The older messaging-side
// AdminActivityLog panel (limit_reached / field_auto_created / error only) is
// retired from the UI but its write path is preserved server-side.
import UnifiedActivityLog from "@/pages/Admin/ActivityLog";
import CsatSettingsPanel from "./CsatSettings";
import ConversationLabelsPanel from "./ConversationLabels";
import ApiKeysPanel from "./ApiKeys";
import McpKeysPanel from "./McpKeys";
import AgentLoginLogs from "@/pages/Admin/AgentLoginLogs";
import TicketPortalPanel from "./TicketPortalPanel";

type Tab =
  | "agentIdle"
  | "customStatuses"
  | "contactPanel"
  | "security"
  | "ipBlocking"
  | "agentIpAllowlist"
  | "agentLoginHistory"
  | "messageScrubbing"
  | "dataRetention"
  | "reports"
  | "activityLog"
  | "csat"
  | "conversationLabels"
  | "apiKeys"
  | "mcpKeys"
  | "ticketPortal";

interface NavItemProps {
  id: Tab;
  label: string;
  active: boolean;
  onClick: (id: Tab) => void;
  badge?: string;
}

function NavItem({ id, label, active, onClick, badge }: NavItemProps) {
  return (
    <button
      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 transition-colors ${
        active
          ? "bg-primary-bg text-primary-active font-medium"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-800"
      }`}
      onClick={() => onClick(id)}
    >
      <span>{label}</span>
      {badge && (
        <span className="text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded font-medium">
          {badge}
        </span>
      )}
    </button>
  );
}

function NavSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 select-none">
        {title}
      </p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

const VALID_TABS = new Set<Tab>([
  "agentIdle", "customStatuses", "contactPanel", "security", "ipBlocking",
  "agentIpAllowlist", "agentLoginHistory", "messageScrubbing", "dataRetention",
  "reports", "activityLog", "csat", "conversationLabels", "apiKeys", "mcpKeys",
  "ticketPortal"
]);

export default function MessagingSettings() {
  const { subscription } = useSubscriptions();
  const [searchParams] = useSearchParams();
  const hasIpBlocking = subscription?.enableIpBlocking ?? false;
  const hasAgentIpRestriction = subscription?.enableAgentIpRestriction ?? false;
  const hasDataRetention = subscription?.enableDataRetention ?? false;
  const hasCustomStatuses = subscription?.enableCustomStatuses ?? false;
  const hasTicketing = subscription?.enableTicketing ?? false;
  const hasReports =
    subscription?.enableConversationReports ||
    subscription?.enableAgentDashboard ||
    subscription?.enableCustomThresholds ||
    subscription?.enableCustomDashboards ||
    subscription?.enableScheduleAdherenceReports ||
    subscription?.enableIvrReports ||
    subscription?.enableCallReports ||
    subscription?.enableMissedChatReports ||
    subscription?.enableAgentPerformanceReports ||
    subscription?.enableResponseTimeReports ||
    subscription?.enableSentimentReports ||
    subscription?.enableAccountUtilizationReports ||
    subscription?.enableAgentAvailabilityReports ||
    subscription?.enableAgentUtilizationReports;
  const hasScrubbing = subscription?.enableMessageScrubbing ?? false;

  const paramTab = searchParams.get("tab") as Tab | null;
  const initialTab: Tab =
    paramTab && VALID_TABS.has(paramTab) ? paramTab : "agentIdle";
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);

  return (
    <div className="h-full flex overflow-hidden">
      {/* ── Left sidebar nav ── */}
      <div className="w-52 shrink-0 border-r border-slate-200 bg-white overflow-y-auto">
        <div className="p-3 space-y-5">
          <NavSection title="Workspace">
            <NavItem id="agentIdle" label="Agent Idle" active={activeTab === "agentIdle"} onClick={setActiveTab} />
            {hasCustomStatuses && (
              <NavItem id="customStatuses" label="Custom Statuses" active={activeTab === "customStatuses"} onClick={setActiveTab} />
            )}
            <NavItem id="contactPanel" label="Contact Panel" active={activeTab === "contactPanel"} onClick={setActiveTab} />
            <NavItem id="conversationLabels" label="Conversation Labels" active={activeTab === "conversationLabels"} onClick={setActiveTab} />
            <NavItem id="csat" label="CSAT Surveys" active={activeTab === "csat"} onClick={setActiveTab} />
            {hasTicketing && (
              <NavItem id="ticketPortal" label="Customer Portal" active={activeTab === "ticketPortal"} onClick={setActiveTab} />
            )}
          </NavSection>

          <NavSection title="Security">
            <NavItem id="security" label="Security & SSO" active={activeTab === "security"} onClick={setActiveTab} />
            {hasIpBlocking && (
              <NavItem id="ipBlocking" label="Visitor IP Blocking" active={activeTab === "ipBlocking"} onClick={setActiveTab} />
            )}
            <NavItem
              id="agentIpAllowlist"
              label="Agent IP Restriction"
              active={activeTab === "agentIpAllowlist"}
              onClick={setActiveTab}
              badge={hasAgentIpRestriction ? undefined : "Upgrade"}
            />
            <NavItem id="agentLoginHistory" label="Agent Login History" active={activeTab === "agentLoginHistory"} onClick={setActiveTab} />
          </NavSection>

          <NavSection title="Compliance">
            <NavItem
              id="messageScrubbing"
              label="Message Scrubbing"
              active={activeTab === "messageScrubbing"}
              onClick={setActiveTab}
              badge={hasScrubbing ? undefined : "Upgrade"}
            />
            <NavItem
              id="dataRetention"
              label="Data Retention"
              active={activeTab === "dataRetention"}
              onClick={setActiveTab}
              badge={hasDataRetention ? undefined : "Upgrade"}
            />
          </NavSection>

          <NavSection title="Developer">
            <NavItem id="apiKeys" label="API Keys" active={activeTab === "apiKeys"} onClick={setActiveTab} />
            <NavItem id="mcpKeys" label="MCP Keys" active={activeTab === "mcpKeys"} onClick={setActiveTab} />
          </NavSection>

          <NavSection title="Reports & Logs">
            {hasReports && (
              <NavItem id="reports" label="Reports" active={activeTab === "reports"} onClick={setActiveTab} />
            )}
            <NavItem id="activityLog" label="Activity Log" active={activeTab === "activityLog"} onClick={setActiveTab} />
          </NavSection>
        </div>
      </div>

      {/* ── Content area ── */}
      <div className="flex-1 overflow-y-auto bg-white">
        {activeTab === "agentIdle" && <AgentIdlePanel />}
        {activeTab === "customStatuses" && hasCustomStatuses && <CustomStatusPanel />}
        {activeTab === "contactPanel" && <ContactPanelSettingsPanel />}
        {activeTab === "security" && <SecurityPanel />}
        {activeTab === "ipBlocking" && hasIpBlocking && <IpBlockingPanel />}
        {activeTab === "agentIpAllowlist" && <AgentIpAllowlistPanel />}
        {activeTab === "agentLoginHistory" && <AgentLoginLogs />}
        {activeTab === "messageScrubbing" && <MessageScrubbingPanel />}
        {activeTab === "dataRetention" && <DataRetentionPanel />}
        {activeTab === "reports" && hasReports && <ReportSettingsPanel />}
        {activeTab === "activityLog" && <UnifiedActivityLog />}
        {activeTab === "csat" && <CsatSettingsPanel />}
        {activeTab === "conversationLabels" && <ConversationLabelsPanel />}
        {activeTab === "apiKeys" && <ApiKeysPanel />}
        {activeTab === "mcpKeys" && <McpKeysPanel />}
        {activeTab === "ticketPortal" && hasTicketing && <TicketPortalPanel />}
      </div>
    </div>
  );
}
